"use client";

import { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { useParams } from "next/navigation";
import Link from "next/link";

type Connection = {
  id: string;
  connection_type: string;
  amount_ars: number;
  amount_usd: number;
  date: string;
  description: string;
  lobby_groups: { name: string; acronym: string } | null;
  evidence: Evidence[] | null;
};

type Evidence = {
  id: string;
  source_url: string;
  source_name: string;
  evidence_type: string;
  excerpt: string;
};

type Politician = {
  id: string;
  name: string;
  position: string;
  jurisdiction: string;
  party: string;
  total_lobby_ars: number;
  total_lobby_usd: number;
  status: string;
  bio: string;
  image_url: string | null;
};

const supabase = () =>
  createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL || "",
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ""
  );

export default function PoliticianPage() {
  const { id } = useParams();
  const [pol, setPol] = useState<Politician | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);

  useEffect(() => {
    if (!id) return;
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    if (!url || !key) return;

    void (async () => {
      try {
        const { data, error } = await supabase()
          .from("politicians")
          .select("*")
          .eq("id", id)
          .single();
        if (!error && data) setPol(data as Politician);

        const { data: connData, error: connError } = await supabase()
          .from("connections")
          .select("*, lobby_groups(name, acronym), evidence(id, source_url, source_name, evidence_type, excerpt)")
          .eq("politician_id", id)
          .order("date", { ascending: false });
        if (!connError) setConnections((connData || []) as Connection[]);
      } catch (_) {
        // silently fail
      }
    })();
  }, [id]);

  if (!pol) return <div className="p-20 text-center text-gray-500">Cargando...</div>;

  const formatArs = (n: number) =>
    n >= 1e9 ? `ARS $${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `ARS $${(n / 1e6).toFixed(1)}M` : `$${n.toLocaleString()}`;
  const formatUsd = (n: number) => `≈ USD $${n.toLocaleString()}`;

  const typeLabels: Record<string, string> = {
    contrato: "📄 CONTRATO",
    viaje: "✈️ VIAJE",
    nombramiento: "👤 NOMBRAMIENTO",
    donacion: "💰 DONACIÓN",
    evento: "🏛️ EVENTO",
    financiamiento: "💵 FINANCIAMIENTO",
  };

  const impactSlugByName: Record<string, string> = {
    "Horacio Rodríguez Larreta": "larreta",
    "Gerardo Werthein": "gerardo-werthein",
    "Javier Milei": "javier-milei",
    "Jorge Macri": "jorge-macri",
    "Patricia Bullrich": "patricia-bullrich",
    "Waldo Wolff": "waldo-wolff",
    "Myriam Bregman": "myriam-bregman",
    "Nicolás del Caño": "nicolas-del-cano",
  };
  const impactSlug = impactSlugByName[pol.name];
  const impactMp4 = impactSlug ? `/media/${impactSlug}-impact-short.mp4` : null;
  const impactGif = impactSlug ? `/media/${impactSlug}-impact-short.gif` : null;
  const isGreen = pol.status !== "red";

  return (
    <main className="min-h-screen bg-black">
      {/* Hero */}
      <section className={`bg-gradient-to-b ${isGreen ? "from-green-950 via-green-900/50" : "from-red-950 via-red-900/50"} to-black px-4 py-12`}>
        <Link href="/" className="text-sm text-gray-500 hover:text-gray-300">← Volver</Link>
        <div className="max-w-3xl mx-auto mt-6 text-center">
          <p className="text-xs uppercase tracking-widest text-gray-500">{pol.jurisdiction} • {pol.party}</p>
          {pol.image_url && (
            <img
              src={pol.image_url}
              alt={`Card verificable de ${pol.name}`}
              className={`mx-auto mt-6 w-full max-w-sm rounded-2xl border ${isGreen ? "border-green-800/70 shadow-green-950/50" : "border-red-800/70 shadow-red-950/50"} shadow-2xl`}
            />
          )}
          <h1 className="text-5xl font-black text-white mt-2">{pol.name.toUpperCase()}</h1>
          <p className="text-xl text-gray-400 mt-2">{pol.position}</p>

          <div className="flex justify-center gap-8 mt-8">
            <div>
              <p className="text-3xl font-black text-white">{formatArs(pol.total_lobby_ars)}</p>
              <p className="text-xs text-red-400 mt-1">{formatUsd(pol.total_lobby_usd)}</p>
            </div>
          </div>

          <span className={`inline-block mt-4 text-sm font-bold px-4 py-2 rounded-full ${isGreen ? "bg-green-900 text-green-300" : "bg-red-900 text-red-300"}`}>
            {isGreen ? "SIN CONEXIONES DOCUMENTADAS" : "ISRAEL LOBBY CONNECTIONS"}
          </span>
        </div>
      </section>

      {/* Impact Reel */}
      {impactMp4 && impactGif && (
        <section className="max-w-3xl mx-auto px-4 pt-8">
          <div className="overflow-hidden rounded-2xl border border-red-800/70 bg-gradient-to-b from-red-950/40 to-white/5 shadow-2xl shadow-red-950/40">
            <div className="border-b border-red-900/50 px-4 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-red-400">
                  Impact Reel
                </p>
                <h2 className="text-xl font-black text-white">SHORT GLITCH EDIT</h2>
              </div>
              <span className="rounded-full bg-red-900 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-red-200">
                MP4 + GIF
              </span>
            </div>

            <div className="grid gap-0 md:grid-cols-[minmax(0,1fr)_230px]">
              <div className="relative bg-black">
                <video
                  className="aspect-[9/16] max-h-[720px] w-full object-cover md:aspect-square"
                  src={impactMp4}
                  poster={impactGif}
                  controls
                  playsInline
                  preload="metadata"
                  aria-label={`Video glitch de impacto sobre ${pol.name}`}
                />
              </div>
              <div className="flex flex-col justify-between border-t border-red-900/50 p-5 md:border-l md:border-t-0">
                <div>
                  <p className="text-sm font-bold text-white">Clip corto para redes</p>
                  <p className="mt-2 text-xs leading-relaxed text-gray-400">
                    Zoom, flashes rojos, glitch, RGB split y scanlines sobre la card verificable.
                    MP4 para web; GIF como fallback/share preview.
                  </p>
                </div>
                <div className="mt-5 flex flex-col gap-2">
                  <a
                    href={impactGif}
                    className="rounded-full bg-red-700 px-4 py-2 text-center text-xs font-black uppercase tracking-wide text-white transition hover:bg-red-600"
                  >
                    Abrir GIF
                  </a>
                  <a
                    href={impactMp4}
                    className="rounded-full border border-red-800 px-4 py-2 text-center text-xs font-black uppercase tracking-wide text-red-200 transition hover:bg-red-950"
                  >
                    Abrir MP4
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Connections */}
      <section className="max-w-3xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white mb-6">
          {isGreen ? "AUDITORÍA DE CONEXIONES 🟢" : "CONEXIONES DOCUMENTADAS 🔴"}
        </h2>

        {connections.length === 0 && isGreen && (
          <div className="rounded-xl border border-green-900/60 bg-green-950/20 p-5">
            <p className="text-lg font-black text-green-200">0 conexiones cooperativas documentadas</p>
            <p className="mt-2 text-sm leading-relaxed text-gray-400">
              No se encontraron registros públicos de plata, contratos, viajes a Israel, reuniones cooperativas,
              procurement de seguridad/defensa ni acuerdos institucionales vinculados al lobby israelí. Las menciones
              públicas detectadas son adversariales o incidentales, por eso no se computan como conexión.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {connections.map((c) => (
            <div key={c.id} className="bg-white/5 border border-red-900/50 rounded-xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="text-xs font-bold text-red-400">
                    {typeLabels[c.connection_type] || c.connection_type.toUpperCase()}
                  </span>
                  {c.lobby_groups && (
                    <span className="ml-2 text-xs text-gray-500">
                      {c.lobby_groups.acronym} — {c.lobby_groups.name}
                    </span>
                  )}
                  <p className="text-white mt-2 font-medium">{c.description}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(c.date).toLocaleDateString("es-AR", { year: "numeric", month: "long" })}
                  </p>
                  {c.evidence && c.evidence.length > 0 ? (
                    <div className="mt-4 space-y-2 border-t border-white/10 pt-3">
                      {c.evidence.map((e) => (
                        <a
                          key={e.id}
                          href={e.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-lg border border-red-900/40 bg-black/30 p-3 transition hover:border-red-600 hover:bg-red-950/20"
                        >
                          <span className="text-xs font-black uppercase tracking-wide text-red-300">
                            Fuente: {e.source_name}
                          </span>
                          {e.evidence_type && (
                            <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase text-gray-300">
                              {e.evidence_type}
                            </span>
                          )}
                          <p className="mt-2 text-xs leading-relaxed text-gray-400">“{e.excerpt}”</p>
                          <p className="mt-2 text-[10px] text-red-400 break-all">{e.source_url}</p>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 rounded-lg border border-yellow-800/50 bg-yellow-950/20 p-3 text-xs text-yellow-200">
                      Pendiente de auditoría: esta conexión todavía necesita una fuente pública clickeable antes de usarse en piezas compartibles.
                    </div>
                  )}
                </div>
                {c.amount_ars > 0 && (
                  <div className="text-right shrink-0">
                    <p className="text-lg font-black text-white">{formatArs(c.amount_ars)}</p>
                    <p className="text-xs text-red-400">{formatUsd(c.amount_usd)}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {connections.length === 0 && (
          <p className="text-gray-600 text-center py-10">Sin conexiones documentadas aún.</p>
        )}

        {/* Sources note */}
        <div className="mt-12 p-4 bg-white/5 border border-gray-800 rounded-lg">
          <p className="text-xs text-gray-500">
            <strong className="text-gray-400">Metodología:</strong> Los datos provienen de fuentes públicas: declaraciones juradas (Oficina Anticorrupción), 
            Cámara Nacional Electoral, contratos BAC/Compr.ar, investigación periodística y registros oficiales. 
            Las cifras en USD son conversiones aproximadas al tipo de cambio blue de la fecha correspondiente. 
            Este sitio no afirma ilegalidad — documenta conexiones para que la ciudadanía saque sus propias conclusiones.
          </p>
        </div>
      </section>

      <footer className="border-t border-red-950 py-8 text-center text-xs text-gray-600">
        <Link href="/">← Lobby Tracker Argentina</Link>
      </footer>
    </main>
  );
}
